import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { FinalCta } from "../components/FinalCta";
import { WORK_ITEMS, BUSINESS, WHY_CHOOSE } from "../content";
import { ShieldCheck, Users, Sparkles, CalendarCheck, MapPin, HeartHandshake } from "lucide-react";

const ICONS = { ShieldCheck, Users, Sparkles, CalendarCheck, MapPin, HeartHandshake };

export const AboutPage = () => (
  <>
    <Seo
      title="About Pure Bliss Clean | Local Cleaners Melbourne South East"
      description={`Pure Bliss Clean is a genuine local cleaning business servicing ${BUSINESS.region} — reliable, detailed cleaning with familiar faces and personal service.`}
      path="/about"
    />
    <PageHero
      eyebrow="About Us"
      titleLines={["Reliable Cleaning.", "Genuine Results.", "Local Service."]}
      intro={`Pure Bliss Clean is a local cleaning business servicing ${BUSINESS.region}. The name says it all — we want every home we clean to feel like pure bliss when you walk back through the door.`}
      image="/assets/work/vanity-after.webp"
      imageAlt="Polished bathroom vanity after a Pure Bliss Clean"
    />

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <Eyebrow>How We Work</Eyebrow>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">A Cleaning Service Built On Trust</h2>
          <div className="mt-6 max-w-lg space-y-4 text-base leading-relaxed text-slate-600">
            <p>
              We're not a big franchise or a faceless booking platform. Pure Bliss Clean is a genuine local business,
              and we take real pride in the work we leave behind — from regular weekly cleans to detailed end-of-lease
              jobs and ongoing commercial arrangements.
            </p>
            <p>
              We aim to keep the same cleaners with our regular clients wherever possible. Familiar faces mean we get
              to know your home, your preferences and exactly how you like things done — and you always know who's
              coming through your door.
            </p>
            <p>
              And if something has genuinely been missed? Let us know promptly and we'll work with you to make it
              right. That's what long-term relationships are built on.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <BeforeAfterSlider {...WORK_ITEMS.find((w) => w.id === "vanity")} />
        </Reveal>
      </div>
    </section>

    <section className="bg-brand-mist">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="max-w-2xl">
          <Eyebrow>What We Stand For</Eyebrow>
          <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">The Pure Bliss Way</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_CHOOSE.map((b, i) => {
            const Icon = ICONS[b.icon];
            return (
              <Reveal key={b.title} delay={0.07 * i}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <span className="inline-flex rounded-2xl bg-teal-50 p-3 text-brand-teal"><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-brand-ink">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
    <FinalCta />
  </>
);
